import { useEffect, useLayoutEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@claritio/frontend/lib/auth'
import { useInterviewContext } from '@claritio/frontend/lib/interviewContext'
import { respondentSidebarCollapsedDefault } from '@claritio/frontend/lib/respondentNav'
import { apiGet, ApiError } from '@claritio/frontend/lib/api'
import { appShellPresence } from '@claritio/frontend/lib/appShellPresence'
import { Toaster } from '@claritio/frontend/components/ui/toast'
import ShellLayout from '@claritio/frontend/components/sidebar/ShellLayout'
import RespondentSidebar from './RespondentSidebar'
import type { InterviewState } from '@claritio/frontend/types'

// ENG-133 — the respondent shell: the interview flow wrapped in the shared
// ShellLayout with a client-first sidebar. No header (language + account live in
// the footer). Collapse is route-driven, not persisted: the rail is tucked away on
// the chat screen and expanded elsewhere, with a manual toggle that lasts until the
// next screen change.
export default function RespondentShell() {
  const { apiPath } = useInterviewContext()
  const { me } = useAuth()
  const location = useLocation()
  const [org, setOrg] = useState<{ name: string; logo_url: string | null } | null>(
    me?.organization ? { name: me.organization.name, logo_url: null } : null,
  )
  const [status, setStatus] = useState<string | undefined>()
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    apiGet<InterviewState>(apiPath)
      .then((s) => { if (s.organization) setOrg(s.organization); setStatus(s.status) })
      // A bad/expired token drops the shell entirely — the page shows its own
      // invalid-link state, never a sidebar wrapped around an empty org.
      .catch((e) => { if (e instanceof ApiError && e.status === 404) setNotFound(true) })
  }, [apiPath])

  // Suppress the global FloatingLanguageToggle — the footer owns language here.
  useLayoutEffect(() => {
    appShellPresence.set(true)
    return () => appShellPresence.set(false)
  }, [])

  // Route-aware collapse: tucked away during the interview, expanded on
  // welcome/done. A manual toggle overrides until the next screen change.
  const routeCollapsed = respondentSidebarCollapsedDefault(location.pathname)
  const [manual, setManual] = useState<boolean | null>(null)
  // Adjusted during render rather than in an effect: React re-runs this
  // component immediately without committing the stale value, so there is no
  // cascading render.
  const [prevRouteCollapsed, setPrevRouteCollapsed] = useState(routeCollapsed)
  if (prevRouteCollapsed !== routeCollapsed) {
    setPrevRouteCollapsed(routeCollapsed)
    setManual(null)
  }
  const collapsed = manual ?? routeCollapsed

  if (notFound) return <Outlet />

  return (
    <ShellLayout
      mobileMode="rail"
      collapsed={collapsed}
      onToggleCollapse={() => setManual(!collapsed)}
      mainClassName="flex-1 min-h-0 overflow-hidden"
      renderSidebar={({ collapsed, canCollapse, onToggleCollapse }) => (
        <RespondentSidebar
          collapsed={collapsed}
          canCollapse={canCollapse}
          onToggleCollapse={onToggleCollapse}
          orgName={org?.name ?? null}
          orgLogoUrl={org?.logo_url ?? null}
          interviewStatus={status}
        />
      )}
      overlays={<Toaster />}
    />
  )
}
